import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { useLocation } from 'react-router-dom';
import FullScreenLogoLoader from '../components/FullScreenLogoLoader/FullScreenLogoLoader';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(
  () => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'),
  { fallback: <FullScreenLogoLoader /> }
);
const BlogPage = loadable(() => import(/* webpackChunkName: "BlogPage" */ '../containers/BlogPage/BlogPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const CurrentEmployerAnswerPage = loadable(() => import(/* webpackChunkName: "CurrentEmployerAnswerPage" */ '../containers/CurrentEmployerAnswerPage/CurrentEmployerAnswerPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const MoreInformationPage = loadable(() => import(/* webpackChunkName: "MoreInformationPage" */ '../containers/MoreInformationPage/MoreInformationPage'));
const UserManagementPage = loadable(() => import(/* webpackChunkName: "UserManagementPage" */ '../containers/UserManagementPage/UserManagementPage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '../containers/DashboardPage/DashboardPage'));
const CalendarPage = loadable(() => import(/* webpackChunkName: "CalendarPage" */ '../containers/CalendarPage/CalendarPage'));
const WalletPage = loadable(() => import(/* webpackChunkName: "WalletPage" */ '../containers/WalletPage/WalletPage'));
const SkillFitInterviewGatePage = loadable(() => import(/* webpackChunkName: "SkillFitInterviewGatePage" */ '../containers/SkillFitInterviewGatePage/SkillFitInterviewGatePage'));
const SkillFitReportPage = loadable(() => import(/* webpackChunkName: "SkillFitReportPage" */ '../containers/SkillFitReportPage/SkillFitReportPage'));
const SkillFitTranscriptPage = loadable(() => import(/* webpackChunkName: "SkillFitTranscriptPage" */ '../containers/SkillFitTranscriptPage/SkillFitTranscriptPage'));
const SkillFitPackagePage = loadable(() => import(/* webpackChunkName: "SkillFitPackagePage" */ '../containers/SkillFitPackagePage/SkillFitPackagePage'));
const SkillFitCandidateFeedbackPage = loadable(() => import(/* webpackChunkName: "SkillFitCandidateFeedbackPage" */ '../containers/SkillFitCandidateFeedbackPage/SkillFitCandidateFeedbackPage'));
const AIInterviewRoomPage = loadable(() => import(/* webpackChunkName: "AIInterviewRoomPage" */ '../containers/AIInterviewRoomPage/AIInterviewRoomPage'));
const AIInterviewWaitingPage = loadable(() => import(/* webpackChunkName: "AIInterviewWaitingPage" */ '../containers/AIInterviewWaitingPage/AIInterviewWaitingPage'));
const JobPostsPage = loadable(() => import(/* webpackChunkName: "JobPostsPage" */ '../containers/JobPostsPage/JobPostsPage'));
const RFPPostsPage = loadable(() => import(/* webpackChunkName: "RFPPostsPage" */ '../containers/RFPPostsPage/RFPPostsPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PaymentsHistoryPage = loadable(() => import(/* webpackChunkName: "PaymentsHistoryPage" */ '../containers/PaymentsHistoryPage/PaymentsHistoryPage'));
const PreferredTalentPage = loadable(() => import(/* webpackChunkName: "PreferredTalentPage" */ '../containers/PreferredTalentPage/PreferredTalentPage'));
const PreferredSuppliersPage = loadable(() => import(/* webpackChunkName: "PreferredSuppliersPage" */ '../containers/PreferredSuppliersPage/PreferredSuppliersPage'));
const MyNetworkPage = loadable(() => import(/* webpackChunkName: "MyNetworkPage" */ '../containers/NetworkInviteePage/MyNetworkPage'));
const NetworkCustomersPage = loadable(() => import(/* webpackChunkName: "NetworkCustomersPage" */ '../containers/NetworkInviteePage/NetworkCustomersPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const SingleBlogPage = loadable(() => import(/* webpackChunkName: "SingleBlogPage" */ '../containers/SingleBlogPage/SingleBlogPage'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));
const PersonaVerificationPage = loadable(() => import(/* webpackChunkName: "PersonaVerificationPage" */ '../containers/PersonaVerification/PersonaVerification'));
const AboutUsPage = loadable(() => import(/* webpackChunkName: "PersonaVerificationPage" */ '../containers/AboutUsPage/AboutUsPage'));
const FAQCustomPage = loadable(() => import(/* webpackChunkName: "PersonaVerificationPage" */ '../containers/FAQCustomPage/FAQCustomPage'));
const PrivacyPolicyCustomPage = loadable(() => import(/* webpackChunkName: "PersonaVerificationPage" */ '../containers/PrivacyPolicyCustomPage/PrivacyPolicyCustomPage'));
const TermsAndConditionsCustomPage = loadable(() => import(/* webpackChunkName: "PersonaVerificationPage" */ '../containers/TermsAndConditionsCustomPage/TermsAndConditionsCustomPage'));
const ShowPresentationPage = loadable(() => import(/* webpackChunkName: "ShowPresentationPage" */ '../containers/ShowPresentationPage/ShowPresentationPage'));
const ViewSharedCredentialsPage = loadable(() => import(/* webpackChunkName: "ViewSharedCredentialsPage" */ '../containers/ViewSharedCredentialsPage/ViewSharedCredentialsPage'));
const TwilioConsentPage = loadable(() => import(/* webpackChunkName: "TwilioConsentPage" */ '../containers/TwilioConsentPage/TwilioConsentPage'));
const PricingsPage = loadable(() => import(/* webpackChunkName: "PricingsPage" */ '../containers/PricingsPage/PricingsPage'));
const SupportPage = loadable(() => import(/* webpackChunkName: "SupportPage" */ '../containers/SupportPage/SupportPage'));


// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'PaymentsHistoryPage',
  'TwilioConsentPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const SearchPage = layoutConfig.searchPage?.variantType === 'map' 
    ? SearchPageWithMap 
    : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/about',
      name: 'AboutUsPage',
      component: AboutUsPage,
      // loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/blogs',
      name: 'BlogPage',
      component: BlogPage,
    },
    {
      path: '/blog/:title',
      name: 'SingleBlogPage',
      component: SingleBlogPage,
      loadData: pageDataLoadingAPI.SingleBlogPage.loadData,
    },
    {
      path: '/faq',
      name: 'FAQCustomPage',
      component: FAQCustomPage,
      // loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditionsCustomPage',
      component: TermsAndConditionsCustomPage,
      loadData: pageDataLoadingAPI.TermsAndConditionsCustomPage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyCustomPage',
      component: PrivacyPolicyCustomPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyCustomPage.loadData,
    },
    {
      path: '/pricings',
      name: 'PricingsPage',
      component: PricingsPage,
    },
    {
      path: '/support',
      name: 'SupportPage',
      component: SupportPage,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: props => {
        
        const location = useLocation();
        const searchParams = new URLSearchParams(location.search);
        
        let jobPosting = searchParams.get('jobPosting') === 'true';

        if(typeof localStorage !== undefined) {
          jobPosting = localStorage.getItem("jobPosting") === 'true';
          
          localStorage.removeItem("jobPosting");
        }

        return (
          <NamedRedirect
            name="EditListingPageNew"
            params={{
              slug: draftSlug,
              id: draftId,
              type: 'new',
              tab: 'details',
              jobPosting: jobPosting ? 'jobPosting' : 'rfpPosting',
            }}
          />
        );
        // return (
        //   <NamedRedirect
        //     name="EditListingPage"
        //     params={{
        //       slug: draftSlug,
        //       id: draftId,
        //       type: 'new',
        //       tab: 'details',
        //       jobPosting,
        //     }}
        //   />
        // );
      },
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:jobPosting',
      name: 'EditListingPageNew',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
      loadData: pageDataLoadingAPI.ProfileSettingsPage.loadData,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      // Public: an employer answers a current-employer request from an emailed link.
      path: '/current-employer/answer',
      name: 'CurrentEmployerAnswerPage',
      component: CurrentEmployerAnswerPage,
    },
    {
      path: '/signuplink',
      name: 'SignupLinkPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signuplink' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/listings',
      name: 'ManageListingsBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="JobPostsPage" params={{ tab: 'job-dashboard' }} />,
    },
    {
      // No longer a page of its own: kept by name because navigation links still point here,
      // and forwarded to the job posts dashboard.
      path: '/listings/:tab',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="JobPostsPage" params={{ tab: 'job-dashboard' }} />,
    },
    {
      path: '/more',
      name: 'MoreInformationBasePage',
      component: () => <NamedRedirect name="MoreInformationPage" params={{ tab: 'work' }} />,
    },
    {
      path: '/more/:tab',
      name: 'MoreInformationPage',
      component: MoreInformationPage,
    },
    {
      path: '/user-manage',
      name: 'UserManagementPage',
      auth: true,
      component: UserManagementPage,
      loadData: pageDataLoadingAPI.UserManagementPage.loadData,
    },
    {
      path: '/dashboard',
      name: 'DashboardPage',
      auth: true,
      component: DashboardPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/calendar',
      name: 'CalendarPage',
      auth: true,
      component: CalendarPage,
      loadData: pageDataLoadingAPI.CalendarPage.loadData,
    },
    {
      // Preferred Network: a customer's Preferred Talent, and a supplier's Consultants.
      path: '/preferred-network/talent',
      name: 'PreferredTalentPage',
      auth: true,
      authPage: 'LoginPage',
      component: PreferredTalentPage,
      loadData: pageDataLoadingAPI.PreferredTalentPage.loadData,
    },
    {
      // Preferred Network: a customer's Preferred Suppliers.
      path: '/preferred-network/suppliers',
      name: 'PreferredSuppliersPage',
      auth: true,
      authPage: 'LoginPage',
      component: PreferredSuppliersPage,
      loadData: pageDataLoadingAPI.PreferredSuppliersPage.loadData,
    },
    {
      // Preferred Network: the customers that invited or linked a supplier.
      path: '/preferred-network/customers',
      name: 'NetworkCustomersPage',
      auth: true,
      authPage: 'LoginPage',
      component: NetworkCustomersPage,
      loadData: pageDataLoadingAPI.NetworkCustomersPage.loadData,
    },
    {
      // A candidate's My Network: the companies and suppliers that invited or linked them.
      path: '/my-network',
      name: 'MyNetworkPage',
      auth: true,
      authPage: 'LoginPage',
      component: MyNetworkPage,
      loadData: pageDataLoadingAPI.MyNetworkPage.loadData,
    },
    {
      path: '/wallet',
      name: 'WalletPage',
      auth: true,
      component: WalletPage,
      loadData: pageDataLoadingAPI.WalletPage.loadData,
    },
    {
      // Screen 10 — the reviewer's SkillFit Check report. Both params are exactly what
      // `GET /api/skillFitReport` already takes: ⛔ there is no report id, because the endpoint
      // resolves the document from (employer org, candidate, job) and its access-control rule is
      // written in those terms. ⚠ `jobListingId` is not optional here — a SkillFit Check belongs to
      // a job application, not to a person.
      path: '/skillfit-report/:candidateListingId/:jobListingId',
      name: 'SkillFitReportPage',
      auth: true,
      authPage: 'LoginPage',
      component: SkillFitReportPage,
      loadData: pageDataLoadingAPI.SkillFitReportPage.loadData,
    },
    {
      // Screen 10a — the full interview transcript, reached from the report's `Read full
      // transcript` link. ⚠ It takes the SAME two ids as the report and ⛔ NO interview id and no
      // report id: `GET /api/skillFitTranscript` runs the report's own access gate and resolves the
      // interview from the entry it finds, so the URL never carries a key that would let a pasted
      // id reach another org's record.
      path: '/skillfit-transcript/:candidateListingId/:jobListingId',
      name: 'SkillFitTranscriptPage',
      auth: true,
      authPage: 'LoginPage',
      component: SkillFitTranscriptPage,
      loadData: pageDataLoadingAPI.SkillFitTranscriptPage.loadData,
    },
    {
      // Screen 11 — the page a client contact opens from a shared SkillFit package. Public: the
      // recipient has no account, and access rests on the link secret, a one-time code and the
      // verified browser's session, all checked by the server. ⚠ No data loader: the secret is in the
      // URL fragment, which never reaches the server, so the page opens the link once it mounts.
      path: '/skillfit-package/:shareId',
      name: 'SkillFitPackagePage',
      component: SkillFitPackagePage,
    },
    {
      // The CANDIDATE's own developmental feedback for one check they sat. ⚠ `skillFitCheckId` is
      // the invitation row's join key, ⛔ NOT a credential and ⛔ not an interview token: the
      // candidate's own check list builds this link from its own row, and the server re-resolves the
      // candidate from the session and searches only that candidate's invitations. A pasted foreign
      // id therefore resolves nothing rather than granting anything.
      // ⛔ Distinct from `/skillfit-report/...` on purpose — that route serves the reviewer's whole
      // document and 403s non-customers; this one serves a projection built for the person assessed.
      path: '/skillfit-feedback/:skillFitCheckId',
      name: 'SkillFitCandidateFeedbackPage',
      auth: true,
      authPage: 'LoginPage',
      component: SkillFitCandidateFeedbackPage,
      loadData: pageDataLoadingAPI.SkillFitCandidateFeedbackPage.loadData,
    },
    {
      path: '/skillfit-interview/:token',
      name: 'SkillFitInterviewGatePage',
      auth: true,
      authPage: 'LoginPage',
      component: SkillFitInterviewGatePage,
      loadData: pageDataLoadingAPI.SkillFitInterviewGatePage.loadData,
    },
    {
      // Screen 5 — the candidate-facing interview room. Its own route (not a fourth `flowStep`
      // inside the gate page) so the room gets an independent mount/unmount lifecycle, which the
      // fullscreen mandate and the proctoring hooks depend on. Routes here are `exact` by default
      // (see Routes.js), so this does not shadow the `/skillfit-interview/:token` gate above.
      // No `loadData`: the join is dispatched from the page's own mount effect, not here. It must
      // run on the client (it ends in a `Room.connect` + mic/cam publish) and it mints a 10-minute
      // participant token, so tying it to the SSR data-loading pass would burn the token's TTL
      // before the browser could use it.
      path: '/skillfit-interview/:token/room',
      name: 'AIInterviewRoomPage',
      auth: true,
      authPage: 'LoginPage',
      component: AIInterviewRoomPage,
    },
    {
      // Screen 1 — the human proctor's waiting screen, and the destination of the proctor's
      // invitation email (`sendSkillFitInvitation.js`). A client route rather than the server
      // handoff route: the screen issues one XHR that performs both server hops, so the short-lived
      // single-use handoff token stays inside `fetch`'s redirect handling and never reaches the
      // address bar, history or referrer of a page a proctor may bookmark.
      // No `loadData`, for the same reason the room route above has none: the resolve runs from the
      // page's own mount effect and mints a 10-minute participant token that must not burn on the
      // SSR pass.
      path: '/skillfit-interview/:token/waiting',
      name: 'AIInterviewWaitingPage',
      auth: true,
      authPage: 'LoginPage',
      component: AIInterviewWaitingPage,
    },
    {
      path: '/jobs',
      name: 'JobPostsBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="JobPostsPage" params={{ tab: 'job-dashboard' }} />,
    },
    {
      path: '/jobs/:tab',
      name: 'JobPostsPage',
      auth: true,
      authPage: 'LoginPage',
      component: JobPostsPage,
      loadData: pageDataLoadingAPI.JobPostsPage.loadData,
    },
    {
      path: '/rfps',
      name: 'JobPostsBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="RFPPostsPage" params={{ tab: 'rfp-dashboard' }} />,
    },
    {
      path: '/rfps/:tab',
      name: 'RFPPostsPage',
      auth: true,
      authPage: 'LoginPage',
      component: RFPPostsPage,
      loadData: pageDataLoadingAPI.RFPPostsPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="StripePayoutPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/msg-sms-consent',
      name: 'TwilioConsentPage',
      auth: true,
      authPage: 'LoginPage',
      component: TwilioConsentPage,
      loadData: pageDataLoadingAPI.TwilioConsentPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/subscriptions',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/subscriptions/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/message-packs',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/account/payments-history',
      name: 'PaymentsHistoryPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentsHistoryPage,
      loadData: pageDataLoadingAPI.PaymentsHistoryPage.loadData,
    },
    // {
    //   path: '/terms-of-service',
    //   name: 'TermsOfServicePage',
    //   component: TermsOfServicePage,
    //   loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    // },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
    {
      path: '/persona-verify',
      name: 'PersonaVerificationBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="PersonaVerificationPage" params={{ tab: 'identity' }} />,
    },
    {
      path: '/persona-verify/:tab',
      name: 'PersonaVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: PersonaVerificationPage,
      loadData: pageDataLoadingAPI.PersonaVerificationPage.loadData,
    },
    {
      path: '/presentation/:requestId',
      name: 'ShowPresentationPage',
      component: ShowPresentationPage,
    },
    {
      path: '/view-credentials/:shareId',
      name: 'ViewSharedCredentialsPage',
      component: ViewSharedCredentialsPage,
    },
  ];
};

export default routeConfiguration;
